import { supabase } from '../supabaseClient';
import { describeError } from '../errorMessage';
import {
  BUCKET_BILDER,
  BUCKET_MEDIEN,
  istFehlendesSchema,
  ladeMediumHoch,
  loescheUnterweisungMedium,
  medienDateiname,
} from './sifaService';
import type { MedienArt } from './sifaService';
import type {
  SifaUnterweisung,
  SifaUnterweisungVorlage,
  UnterweisungAbschnitt,
  UnterweisungFrage,
  UnterweisungStatus,
} from './types';
import {
  VORLAGEN_ORDNER,
  medienPfade,
  pfadeUmschreiben,
  pfadImOrdner,
  vorlageMedienLoeschbar,
  vorlagenOrdner,
} from './vorlagenLogic';

/**
 * Datenbankzugriff der globalen Unterweisungs-Vorlagen je Firma — Tabellen
 * `sifa_unterweisung_vorlagen` und `sifa_fachkraefte` (Nr. 141), Medien im
 * Ordner `vorlagen/<id>/` des Buckets `sifa-unterweisung-medien`. Vertrag:
 * docs/sifa-vorlagen-plan.md §8.3; die Oberflaeche codiert dagegen.
 *
 * Muster sifaService.ts, mit drei Riegeln:
 *
 * (a) OPTIMISTISCHE SPERRE. updateVorlage schreibt nur `where fassung =
 *     <gelesene Fassung>` und zaehlt sie dabei hoch. Zwei Bearbeiter
 *     ueberschreiben sich so nicht: der zweite bekommt VorlageKonfliktError.
 *
 * (b) FREIGEGEBEN BLEIBT. Ab `erstmals_freigegeben_am` kann es Kundenkopien
 *     geben, die auf die Dateien der Vorlage zeigen: kein Loeschen der
 *     Vorlage, kein Loeschen ihrer Medien — nur archivieren. Die Policies aus
 *     Nr. 141 pruefen dasselbe serverseitig.
 *
 * (c) EIGENTUM NACH ORDNER. Medien werden nur im eigenen Ordner geloescht
 *     (pfadImOrdner); eine Datei woanders gehoert jemand anderem.
 *
 * FAIL-OPEN: solange Nr. 141 nicht gefahren ist, liefern die Lesewege leere
 * Listen, null, false bzw. eine leere Map (42P01/PGRST205, bei `vorlage_id`
 * auch 42703). Der Menuepunkt bleibt dann leer, die Kundenseite laeuft wie
 * bisher. Andere Lesefehler werfen — ausser bei istSifaFachkraft und
 * zaehleKundenJeVorlage: das sind Anzeige-Hinweise, kein Riegel (RLS
 * entscheidet serverseitig). Schreibwege werfen immer.
 */

export const T_VORLAGEN = 'sifa_unterweisung_vorlagen';
export const T_FACHKRAEFTE = 'sifa_fachkraefte';
const T_UNTERWEISUNGEN = 'sifa_unterweisungen';

const KONFLIKT_MELDUNG = 'Diese Vorlage wurde inzwischen von jemand anderem geändert. Bitte neu laden.';
const KEIN_RECHT_VORLAGE = 'Vorlagen bearbeiten dürfen nur Admins und Nutzer mit Häkchen „SiFa".';
const KEIN_RECHT_FACHKRAFT = 'Das Häkchen „SiFa" setzen nur Admins.';
const KEIN_RECHT_LESEN = 'Die Vorlagen sind für dieses Konto nicht lesbar.';

/** Seitengroesse beim Zaehlen — PostgREST liefert ohne `range` hoechstens 1000 Zeilen. */
const SEITE = 1000;

/** Die optimistische Sperre griff: ein anderer Stand wurde inzwischen gespeichert. */
export class VorlageKonfliktError extends Error {
  constructor(message: string = KONFLIKT_MELDUNG) {
    super(message);
    this.name = 'VorlageKonfliktError';
  }
}

// ─── Fehler ─────────────────────────────────────────────────────────────────

const errorCode = (e: unknown): string =>
  typeof e === 'object' && e !== null ? String((e as { code?: unknown }).code ?? '') : '';

/** 42703 = Spalte fehlt (`vorlage_id` ohne Nr. 141); PGRST204 = dasselbe aus dem Schema-Cache. */
const istFehlendeSpalte = (e: unknown): boolean => {
  const code = errorCode(e);
  return code === '42703' || code === 'PGRST204';
};

/** 23505 = unique_violation, hier: das Haekchen ist schon gesetzt. */
const istUniqueVerletzung = (e: unknown): boolean => errorCode(e) === '23505';

/**
 * Wirft lesbar. 42501 (RLS) und ein fehlendes Schema bekommen Klartext
 * davor — „new row violates row-level security policy" erklaert niemandem,
 * dass das Haekchen fehlt.
 */
const werfe = (e: unknown, ohneRecht = KEIN_RECHT_VORLAGE): never => {
  if (errorCode(e) === '42501') throw new Error(`${ohneRecht} (${describeError(e)})`);
  if (istFehlendesSchema(e)) throw new Error('Die Vorlagen sind noch nicht eingerichtet (Migration Nr. 141 fehlt).');
  throw new Error(describeError(e));
};

// ─── Mapper snake → camel ───────────────────────────────────────────────────

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : str(v));
const num = (v: unknown, fallback: number): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};
const numOrNull = (v: unknown): number | null => (v == null ? null : num(v, 0));
const arr = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
/** Datum/Zeitstempel ohne Umrechnung — die Anzeige formatiert selbst. */
const iso = (v: unknown): string => str(v);

/** Leere Zeichenkette → null (Spalten sind NULL-bar; ein '' ist dort eine Luege). */
const leerZuNull = (v: string | null | undefined): string | null => {
  const t = (v ?? '').trim();
  return t ? t : null;
};

export const mapVorlage = (row: Record<string, unknown>): SifaUnterweisungVorlage => ({
  id: str(row.id),
  titel: str(row.titel),
  beschreibung: strOrNull(row.beschreibung),
  kategorie: strOrNull(row.kategorie),
  inhalt: arr<UnterweisungAbschnitt>(row.inhalt),
  fragen: arr<UnterweisungFrage>(row.fragen),
  bestehenProzent: num(row.bestehen_prozent, 80),
  gueltigMonate: num(row.gueltig_monate, 12),
  dauerMin: numOrNull(row.dauer_min),
  sprache: str(row.sprache) || 'de',
  status: (str(row.status) || 'entwurf') as UnterweisungStatus,
  fassung: num(row.fassung, 1),
  erstmalsFreigegebenAm: strOrNull(row.erstmals_freigegeben_am),
  erstelltVon: strOrNull(row.erstellt_von),
  erstelltVonName: strOrNull(row.erstellt_von_name),
  geaendertVonName: strOrNull(row.geaendert_von_name),
  companyId: strOrNull(row.company_id),
  companyName: strOrNull(row.company_name),
  createdAt: iso(row.created_at),
  updatedAt: iso(row.updated_at),
});

// ─── Hilfen ─────────────────────────────────────────────────────────────────

type Profil = { id: string | null; name: string | null; companyId: string | null; companyName: string | null };

/**
 * Angemeldeter Nutzer mit Firma — best effort. Die Firma setzt der Client,
 * die Policy prueft sie im `with check` gegen current_company_id().
 */
const eigenesProfil = async (): Promise<Profil> => {
  const leer: Profil = { id: null, name: null, companyId: null, companyName: null };
  try {
    const { data: auth } = await supabase.auth.getUser();
    const user = auth?.user;
    if (!user?.id) return leer;
    const { data } = await supabase
      .from('profiles')
      .select('first_name, last_name, company_id, company_name')
      .eq('id', user.id)
      .maybeSingle();
    const p = (data ?? null) as { first_name?: string; last_name?: string; company_id?: unknown; company_name?: string } | null;
    const name = ([p?.first_name, p?.last_name].filter(Boolean).join(' ') || user.email || '').trim();
    return {
      id: user.id,
      name: name || null,
      companyId: leerZuNull(strOrNull(p?.company_id)),
      companyName: leerZuNull(p?.company_name),
    };
  } catch {
    // Anzeige und Vorbelegung, kein Riegel — die Policy entscheidet.
    return leer;
  }
};

// ═══ Lesen ════════════════════════════════════════════════════════════════

/**
 * Die Vorlagen der eigenen Firma, nach Titel. Ohne `eq` auf die Firma: das
 * erledigt die RLS-Policy, verlaesslicher als ein Filter, den man vergessen
 * kann. `nurFreigegebene` = Auswahl beim Kunden (nur 'aktiv').
 */
export const fetchVorlagen = async (opts?: { nurFreigegebene?: boolean }): Promise<SifaUnterweisungVorlage[]> => {
  try {
    let abfrage = supabase.from(T_VORLAGEN).select('*');
    if (opts?.nurFreigegebene) abfrage = abfrage.eq('status', 'aktiv');
    const { data, error } = await abfrage.order('titel', { ascending: true });
    if (error) throw error;
    return arr<Zeile>(data).map((r) => mapVorlage(r));
  } catch (e) {
    if (istFehlendesSchema(e)) {
      console.warn('sifa_unterweisung_vorlagen fehlt — Migration Nr. 141 noch nicht gefahren.');
      return [];
    }
    return werfe(e, KEIN_RECHT_LESEN);
  }
};

/** Eine Vorlage; null, wenn es sie nicht gibt, RLS sie verbirgt oder Nr. 141 fehlt. */
export const fetchVorlage = async (id: string): Promise<SifaUnterweisungVorlage | null> => {
  const vid = (id || '').trim();
  if (!vid) return null;
  try {
    const { data, error } = await supabase.from(T_VORLAGEN).select('*').eq('id', vid).maybeSingle();
    if (error) throw error;
    return data ? mapVorlage(data as Zeile) : null;
  } catch (e) {
    if (istFehlendesSchema(e)) return null;
    return werfe(e, KEIN_RECHT_LESEN);
  }
};

// ═══ Schreiben ════════════════════════════════════════════════════════════

/** Was der Aufrufer beim Anlegen setzen darf; Status, Fassung, Firma und Zeitstempel vergibt dieses Modul. */
export type NeueVorlage = Partial<
  Pick<
    SifaUnterweisungVorlage,
    'titel' | 'beschreibung' | 'kategorie' | 'inhalt' | 'fragen' | 'bestehenProzent' | 'gueltigMonate' | 'dauerMin' | 'sprache'
  >
>;

/** Was sich am Inhalt einer Vorlage aendern laesst — der Status geht ueber setzeVorlagenStatus. */
export type VorlagePatch = NeueVorlage;

/** Legt eine Vorlage als ENTWURF in Fassung 1 an; Firma und Ersteller aus dem Profil. */
export const createVorlage = async (entwurf: NeueVorlage): Promise<SifaUnterweisungVorlage> => {
  const titel = (entwurf?.titel ?? '').trim();
  if (!titel) throw new Error('Bitte einen Titel für die Vorlage eingeben.');
  const profil = await eigenesProfil();

  const zeile: Zeile = {
    titel,
    beschreibung: leerZuNull(entwurf.beschreibung),
    kategorie: leerZuNull(entwurf.kategorie),
    inhalt: entwurf.inhalt ?? [],
    fragen: entwurf.fragen ?? [],
    bestehen_prozent: entwurf.bestehenProzent ?? 80,
    gueltig_monate: entwurf.gueltigMonate ?? 12,
    dauer_min: entwurf.dauerMin ?? null,
    sprache: (entwurf.sprache ?? '').trim() || 'de',
    status: 'entwurf',
    fassung: 1,
    erstellt_von_name: profil.name,
    geaendert_von_name: profil.name,
    company_name: profil.companyName,
  };
  // Nur, wenn bekannt: sonst greifen die Spaltenvorgaben (auth.uid(),
  // current_company_id()). Ein null hier hebelte die Vorgabe aus, und die
  // Policy wiese die Zeile ab.
  if (profil.id) zeile.erstellt_von = profil.id;
  if (profil.companyId) zeile.company_id = profil.companyId;

  const { data, error } = await supabase.from(T_VORLAGEN).insert(zeile).select('*').single();
  if (error) return werfe(error);
  return mapVorlage(data as Zeile);
};

const patchZuZeile = (patch: VorlagePatch): Zeile => {
  const z: Zeile = {};
  if (patch.titel !== undefined) z.titel = (patch.titel ?? '').trim();
  if (patch.beschreibung !== undefined) z.beschreibung = leerZuNull(patch.beschreibung);
  if (patch.kategorie !== undefined) z.kategorie = leerZuNull(patch.kategorie);
  if (patch.inhalt !== undefined) z.inhalt = patch.inhalt ?? [];
  if (patch.fragen !== undefined) z.fragen = patch.fragen ?? [];
  if (patch.bestehenProzent !== undefined) z.bestehen_prozent = patch.bestehenProzent;
  if (patch.gueltigMonate !== undefined) z.gueltig_monate = patch.gueltigMonate;
  if (patch.dauerMin !== undefined) z.dauer_min = patch.dauerMin ?? null;
  if (patch.sprache !== undefined) z.sprache = (patch.sprache ?? '').trim() || 'de';
  return z;
};

/**
 * Speichert den Inhalt einer Vorlage im Status `entwurf` oder `aktiv` —
 * Riegel (a): `.eq('fassung', fassungAlt)` im UPDATE selbst, neue Fassung =
 * fassungAlt + 1. Kommt keine Zeile zurueck, wird nachgelesen, damit die
 * Meldung stimmt: nicht gefunden, archiviert (eingefroren), anderer Stand
 * (VorlageKonfliktError) oder — gleiche Fassung, trotzdem nichts — fehlendes
 * Recht (RLS filtert stumm, statt zu werfen).
 */
export const updateVorlage = async (id: string, fassungAlt: number, patch: VorlagePatch): Promise<SifaUnterweisungVorlage> => {
  const vid = (id || '').trim();
  if (!vid) throw new Error('Keine Vorlage gewählt.');
  if (!Number.isInteger(fassungAlt) || fassungAlt < 1) throw new Error('Die Fassung der Vorlage ist unbekannt. Bitte neu laden.');
  const zeile = patchZuZeile(patch ?? {});
  if (typeof zeile.titel === 'string' && !zeile.titel) throw new Error('Der Titel darf nicht leer sein.');

  const profil = await eigenesProfil();
  zeile.fassung = fassungAlt + 1;
  if (profil.name) zeile.geaendert_von_name = profil.name;
  zeile.updated_at = new Date().toISOString();

  const { data, error } = await supabase
    .from(T_VORLAGEN)
    .update(zeile)
    .eq('id', vid)
    .eq('fassung', fassungAlt)
    .in('status', ['entwurf', 'aktiv'])
    .select('*');
  if (error) return werfe(error);
  if (data && data.length > 0) return mapVorlage(data[0] as Zeile);

  const vorhanden = await fetchVorlage(vid);
  if (!vorhanden) throw new Error('Diese Vorlage wurde nicht gefunden (Speichern).');
  if (vorhanden.status === 'archiviert') {
    throw new Error('Diese Vorlage ist archiviert und damit schreibgeschützt. Für Änderungen bitte erst wieder freigeben.');
  }
  if (vorhanden.fassung !== fassungAlt) throw new VorlageKonfliktError();
  throw new Error(`Die Vorlage konnte nicht gespeichert werden. ${KEIN_RECHT_VORLAGE}`);
};

const STATUS_WERTE: readonly UnterweisungStatus[] = ['entwurf', 'aktiv', 'archiviert'];

/**
 * Statuswechsel OHNE Fassungssprung — Kundenkopien sehen keine „neue
 * Fassung", nur weil freigegeben oder archiviert wurde.
 *
 * Gebunden an den Stand, den der Nutzer gerade SIEHT — Fassung UND Status
 * (`fassungErwartet`, `statusErwartet`). Die Fassung allein reicht nicht:
 * ein Statuswechsel zaehlt sie nicht hoch, und ohne den Status gaebe ein
 * veralteter Editor eine Vorlage wieder frei, die ein anderer gerade
 * archiviert hat — beides saehe nach Erfolg aus. Ebenso gaebe er einen
 * Inhalt frei, den ein anderer inzwischen gespeichert und niemand hier
 * geprueft hat. 0 Zeilen werden nachgelesen: nicht gefunden, Fassung oder
 * Status weicht ab (VorlageKonfliktError) oder fehlendes Recht.
 *
 * Bei 'aktiv' setzt ein ZWEITES Update `erstmals_freigegeben_am`, aber nur
 * `where erstmals_freigegeben_am is null`: der Zeitpunkt sperrt ab jetzt
 * Loeschen und Medien-Loeschen (Riegel b) und darf sich nie verschieben. Ab
 * Nr. 142 setzt ein Trigger ihn atomar mit dem Statuswechsel selbst; dann
 * trifft dieser Request 0 Zeilen und ist harmlos. Er bleibt, damit die Sperre
 * auch auf einer Datenbank ohne Nr. 142 entsteht. Scheitert er, wirft die
 * Funktion — eine freigegebene Vorlage ohne Sperre waere loeschbar, waehrend
 * schon Kopien auf ihre Dateien zeigen. Rueckgabe frisch gelesen.
 */
export const setzeVorlagenStatus = async (
  id: string,
  status: UnterweisungStatus,
  fassungErwartet: number,
  statusErwartet: UnterweisungStatus,
): Promise<SifaUnterweisungVorlage> => {
  const vid = (id || '').trim();
  if (!vid) throw new Error('Keine Vorlage gewählt.');
  if (!STATUS_WERTE.includes(status)) throw new Error(`Unbekannter Status „${String(status)}".`);
  if (!Number.isInteger(fassungErwartet) || fassungErwartet < 1) {
    throw new Error('Die Fassung der Vorlage ist unbekannt. Bitte neu laden.');
  }
  if (!STATUS_WERTE.includes(statusErwartet)) throw new Error('Der Status der Vorlage ist unbekannt. Bitte neu laden.');

  const profil = await eigenesProfil();
  const jetzt = new Date().toISOString();
  const zeile: Zeile = { status, updated_at: jetzt };
  if (profil.name) zeile.geaendert_von_name = profil.name;

  const { data, error } = await supabase
    .from(T_VORLAGEN)
    .update(zeile)
    .eq('id', vid)
    .eq('fassung', fassungErwartet)
    .eq('status', statusErwartet)
    .select('id');
  if (error) return werfe(error);
  if (!data || data.length === 0) {
    const vorhanden = await fetchVorlage(vid);
    if (!vorhanden) throw new Error('Diese Vorlage wurde nicht gefunden (Status).');
    if (vorhanden.fassung !== fassungErwartet || vorhanden.status !== statusErwartet) throw new VorlageKonfliktError();
    throw new Error(`Der Status konnte nicht geändert werden. ${KEIN_RECHT_VORLAGE}`);
  }

  if (status === 'aktiv') {
    const { error: fehlerFreigabe } = await supabase
      .from(T_VORLAGEN)
      .update({ erstmals_freigegeben_am: jetzt })
      .eq('id', vid)
      .is('erstmals_freigegeben_am', null);
    if (fehlerFreigabe) return werfe(fehlerFreigabe);
  }

  const frisch = await fetchVorlage(vid);
  if (!frisch) throw new Error('Diese Vorlage wurde nicht gefunden (Status).');
  return frisch;
};

/**
 * Raeumt den Medienordner `vorlagen/<id>/` — seitenweise gelistet, auch
 * hochgeladene, nie gespeicherte Dateien. Nur Pfade im eigenen Ordner. Best
 * effort: ein Rest im Bucket stoert niemanden.
 */
const raeumeMedienordner = async (vorlageId: string): Promise<void> => {
  const ordner = vorlagenOrdner(vorlageId);
  const pfade: string[] = [];
  try {
    for (let ab = 0; ; ab += SEITE) {
      const { data, error } = await supabase.storage.from(BUCKET_MEDIEN).list(ordner, { limit: SEITE, offset: ab });
      if (error) break;
      const dateien = arr<{ name?: unknown }>(data);
      for (const datei of dateien) {
        const pfad = `${ordner}/${str(datei?.name)}`;
        if (pfadImOrdner(pfad, ordner)) pfade.push(pfad);
      }
      if (dateien.length < SEITE) break;
    }
  } catch {
    // Ohne Liste bleibt der Ordner liegen — die Zeile ist trotzdem weg.
  }
  for (let ab = 0; ab < pfade.length; ab += SEITE) {
    try {
      await supabase.storage.from(BUCKET_MEDIEN).remove(pfade.slice(ab, ab + SEITE));
    } catch {
      // best effort
    }
  }
};

/**
 * Loescht einen Vorlagen-ENTWURF (docs/sifa-ausbau2-plan.md §0.6): nie
 * freigegeben ODER von nichts referenziert — keine Kundenkopie mit
 * `vorlage_id`, kein Inhalt einer Kunden-Unterweisung oder anderen Vorlage,
 * der auf `vorlagen/<id>/` zeigt. Das kann nur die Datenbank pruefen (sie
 * sieht alle Projekte): die Delete-Policy aus Nr. 144 ruft dafuer
 * `sifa_vorlage_loeschbar`. Der Client filtert deshalb nur noch auf
 * `status = 'entwurf'` und laesst die Policy entscheiden; ohne Nr. 144 gilt
 * die alte Policy (nur nie freigegeben).
 *
 * Reihenfolge wie gehabt: ZUERST die Zeile — atomar, die Bedingung sitzt im
 * DELETE selbst —, DANACH die Dateien. Trifft das DELETE 0 Zeilen, wird KEINE
 * Datei angefasst; nachgelesen wird nur fuer den Klartext. Ist die Zeile weg,
 * kann keine Kopie mehr entstehen, und referenziert war sie laut Datenbank
 * nicht — der Ordner gehoert niemandem mehr (Storage-Policies: Paket A).
 *
 * true = geloescht, false = gibt es nicht (mehr); sonst Error mit Klartext.
 */
export const deleteVorlageEntwurf = async (id: string): Promise<boolean> => {
  const vid = (id || '').trim();
  if (!vid) return false;

  const { data, error } = await supabase
    .from(T_VORLAGEN)
    .delete()
    .eq('id', vid)
    .eq('status', 'entwurf')
    .select('id');
  if (error) return werfe(error);
  if (!data || data.length === 0) {
    const vorhanden = await fetchVorlage(vid);
    if (!vorhanden) return false;
    if (vorhanden.status !== 'entwurf') {
      throw new Error('Nur Entwürfe lassen sich löschen — diese Vorlage ist freigegeben oder archiviert. Bitte archivieren statt löschen.');
    }
    if (vorhanden.erstmalsFreigegebenAm) throw new Error(MELDUNG_VORLAGE_IN_VERWENDUNG);
    throw new Error(`Die Vorlage konnte nicht gelöscht werden. ${KEIN_RECHT_VORLAGE}`);
  }

  await raeumeMedienordner(vid);
  return true;
};

const MELDUNG_VORLAGE_IN_VERWENDUNG =
  'Diese Vorlage war schon freigegeben und wird noch verwendet — von Kundenkopien oder über Verweise auf ihre Dateien. Sie lässt sich nicht löschen, nur archivieren.';

/**
 * Warum „Löschen" bei dieser Vorlage nicht geht — fuer die Oberflaeche; null
 * = Loeschen anbieten. `kopien` = Kunden mit Kopie (zaehleKundenJeVorlage,
 * soweit per RLS sichtbar). Ein Vorab-Hinweis, kein Riegel: ob eine einmal
 * freigegebene Vorlage wirklich frei ist (auch Verweise auf ihre Dateien),
 * entscheidet erst die Datenbank beim Loeschen — dann mit Klartext.
 */
export const vorlageLoeschbarHinweis = (
  v: Pick<SifaUnterweisungVorlage, 'status' | 'erstmalsFreigegebenAm'>,
  kopien: number,
): string | null => {
  const n = Number.isFinite(kopien) && kopien > 0 ? Math.floor(kopien) : 0;
  if (n > 0 && (v.status !== 'entwurf' || v.erstmalsFreigegebenAm)) {
    return `${n === 1 ? 'Bei einem Kunden' : `Bei ${n} Kunden`} in Verwendung — nicht löschbar, nur archivierbar.`;
  }
  if (v.status !== 'entwurf') return 'Löschen geht nur im Entwurf.';
  return null;
};

// ═══ Medien ═══════════════════════════════════════════════════════════════

/**
 * Laedt ein Bild oder Video in den Ordner der Vorlage:
 * `vorlagen/<vorlageId>/<uuid>.<endung>`. Typ, Groesse und Fortschritt wie
 * uploadUnterweisungMedium (gemeinsamer Kern ladeMediumHoch).
 */
export const uploadVorlagenMedium = async (
  vorlageId: string,
  file: File | Blob,
  onFortschritt?: (geladen: number, gesamt: number) => void,
): Promise<{ path: string; art: MedienArt }> => {
  const vid = (vorlageId || '').trim();
  if (!vid || vid.includes('/')) throw new Error('Ohne Vorlage lässt sich keine Datei ablegen.');
  if (!file || file.size <= 0) throw new Error('Die Datei ist leer.');
  return ladeMediumHoch(`${vorlagenOrdner(vid)}/${medienDateiname(file)}`, file, onFortschritt);
};

/**
 * Loescht ein Medium der Vorlage — nur im eigenen Ordner und nur, solange die
 * Vorlage nie freigegeben war (Riegel b, c); sonst still nichts. Danach
 * zeigen womoeglich Kundenkopien auf die Datei; ein Rest im Bucket ist der
 * kleinere Schaden (Aufraeumlauf Stufe 2). Speicherfehler werden gemeldet.
 */
export const loescheVorlagenMedium = async (v: SifaUnterweisungVorlage, pfad: string): Promise<void> => {
  if (!v || !pfadImOrdner(pfad, vorlagenOrdner(v.id)) || !vorlageMedienLoeschbar(v)) return;
  await loescheUnterweisungMedium(pfad);
};

// ═══ Kopien ═══════════════════════════════════════════════════════════════

/**
 * vorlage_id → Zahl der KUNDEN (verschiedene Projekte) mit mindestens einer
 * Kopie, soweit per RLS sichtbar, seitenweise gelesen. Zwei Kopien beim
 * selben Kunden zaehlen einmal — die Ansicht sagt „bei N Kunden".
 * Anzeige, kein Riegel: jeder Fehler liefert eine leere Map; fehlende
 * Tabelle oder Spalte (42703, Nr. 141 offen) still.
 */
export const zaehleKundenJeVorlage = async (): Promise<Map<string, number>> => {
  const projekte = new Map<string, Set<string>>();
  try {
    for (let ab = 0; ; ab += SEITE) {
      const { data, error } = await supabase
        .from(T_UNTERWEISUNGEN)
        .select('vorlage_id, project_id')
        .not('vorlage_id', 'is', null)
        .order('id', { ascending: true })
        .range(ab, ab + SEITE - 1);
      if (error) throw error;
      const zeilen = arr<{ vorlage_id?: unknown; project_id?: unknown }>(data);
      for (const z of zeilen) {
        const vid = str(z?.vorlage_id).trim();
        if (!vid) continue;
        const menge = projekte.get(vid) ?? new Set<string>();
        menge.add(str(z?.project_id).trim());
        projekte.set(vid, menge);
      }
      if (zeilen.length < SEITE) break;
    }
    return new Map([...projekte].map(([vid, menge]) => [vid, menge.size]));
  } catch (e) {
    if (!istFehlendesSchema(e) && !istFehlendeSpalte(e)) {
      console.warn('Kunden je Vorlage nicht zählbar:', describeError(e));
    }
    return new Map();
  }
};

// ═══ Häkchen „SiFa" ═══════════════════════════════════════════════════════

/**
 * Hat der Nutzer das Haekchen „SiFa" (Zeile in `sifa_fachkraefte`)? Ohne
 * Argument der angemeldete Nutzer. Steuert nur, was die Oberflaeche anbietet
 * — schreiben laesst die Policy trotzdem nur Admin oder Fachkraft. Deshalb
 * fail-open `false` bei JEDEM Fehler: der Start der App haengt nicht daran.
 */
export const istSifaFachkraft = async (userId?: string): Promise<boolean> => {
  try {
    let uid = (userId ?? '').trim();
    if (!uid) {
      const { data: auth } = await supabase.auth.getUser();
      uid = str(auth?.user?.id).trim();
    }
    if (!uid) return false;
    const { data, error } = await supabase.from(T_FACHKRAEFTE).select('user_id').eq('user_id', uid).maybeSingle();
    if (error) throw error;
    return !!data;
  } catch (e) {
    if (!istFehlendesSchema(e)) console.warn('Häkchen „SiFa" nicht lesbar:', describeError(e));
    return false;
  }
};

/**
 * Alle user_ids mit Haekchen (soweit per RLS sichtbar — Admins sehen alle).
 * Fehlende Tabelle → leer; andere Fehler werfen, denn eine leere Spalte in
 * der Benutzerverwaltung saehe aus wie „niemand hat es".
 */
export const fetchSifaFachkraefte = async (): Promise<Set<string>> => {
  try {
    const { data, error } = await supabase.from(T_FACHKRAEFTE).select('user_id');
    if (error) throw error;
    return new Set(arr<{ user_id?: unknown }>(data).map((z) => str(z?.user_id).trim()).filter(Boolean));
  } catch (e) {
    if (istFehlendesSchema(e)) return new Set();
    return werfe(e, KEIN_RECHT_FACHKRAFT);
  }
};

/** Firma des Ziel-Nutzers fuer die Zeile — best effort, sonst die eigene. */
const firmaVon = async (userId: string): Promise<string | null> => {
  try {
    const { data } = await supabase.from('profiles').select('company_id').eq('id', userId).maybeSingle();
    return leerZuNull(strOrNull((data as { company_id?: unknown } | null)?.company_id));
  } catch {
    return null;
  }
};

/**
 * Setzt oder entfernt das Haekchen „SiFa" (nur Admins, Policy Nr. 141).
 * Insert statt Upsert: es gibt bewusst keine Update-Policy. Schon gesetzt
 * (23505 — zweiter Klick, zweiter Admin) ist das gewuenschte Ergebnis.
 */
export const setzeSifaFachkraft = async (userId: string, an: boolean): Promise<void> => {
  const uid = (userId || '').trim();
  if (!uid) throw new Error('Kein Nutzer gewählt.');
  try {
    if (!an) {
      const { error } = await supabase.from(T_FACHKRAEFTE).delete().eq('user_id', uid);
      if (error) throw error;
      return;
    }
    const [ich, firma] = await Promise.all([eigenesProfil(), firmaVon(uid)]);
    const zeile: Zeile = { user_id: uid, company_id: firma ?? ich.companyId };
    // Ohne bekannte eigene id greift die Spaltenvorgabe auth.uid().
    if (ich.id) zeile.gesetzt_von = ich.id;
    const { error } = await supabase.from(T_FACHKRAEFTE).insert(zeile);
    if (error && !istUniqueVerletzung(error)) throw error;
  } catch (e) {
    return werfe(e, KEIN_RECHT_FACHKRAFT);
  }
};

// ═══ Kunden-Unterweisung → Vorlage ════════════════════════════════════════

/** Endung aus einem Pfad; ohne erkennbare nach der Rolle im Inhalt (Video → mp4, sonst jpg). */
const endungFuer = (pfad: string, istVideo: boolean): string => {
  const treffer = /\.([a-zA-Z0-9]{1,8})$/.exec(pfad.split('/').pop() ?? '');
  return treffer ? treffer[1].toLowerCase() : istVideo ? 'mp4' : 'jpg';
};

/** „Abschnitt 2 „Fluchtwege": das Video x.mp4" — damit der Hinweis sagt, WO etwas fehlt. */
const beschreibeDatei = (inhalt: UnterweisungAbschnitt[], pfad: string): string => {
  const index = inhalt.findIndex((a) => a?.bildPfad === pfad || a?.videoPfad === pfad);
  const datei = pfad.split('/').pop() || pfad;
  if (index < 0) return `Die Datei ${datei}`;
  const a = inhalt[index];
  const titel = (a.titel || '').trim();
  const art = a.videoPfad === pfad ? 'das Video' : 'das Bild';
  return `Abschnitt ${index + 1}${titel ? ` „${titel}"` : ''}: ${art} ${datei}`;
};

/**
 * Kopiert eine Datei in den Vorlagenordner. Drei Segmente = Medien-Bucket →
 * `storage.copy` (serverseitig, auch fuer 1-GB-Videos ohne Umweg ueber den
 * Browser). Zwei Segmente = alter privater Bilder-Bucket (Nr. 82) → kein
 * Copy ueber Buckets hinweg, also herunterladen und neu hochladen.
 */
const kopiereMedium = async (alt: string, neu: string): Promise<void> => {
  const segmente = alt.split('/').length;
  if (segmente === 3) {
    const { error } = await supabase.storage.from(BUCKET_MEDIEN).copy(alt, neu);
    if (error) throw error;
    return;
  }
  if (segmente === 2) {
    const { data, error } = await supabase.storage.from(BUCKET_BILDER).download(alt);
    if (error) throw error;
    if (!data) throw new Error('Die Datei ist leer.');
    await ladeMediumHoch(neu, data as Blob);
    return;
  }
  throw new Error('unbekannter Ablageort');
};

/**
 * Macht aus einer bestehenden Kunden-Unterweisung einen neuen
 * Vorlagen-ENTWURF (Kategorie leer) — fuer Kurse, die es schon gibt.
 *
 * Ablauf: zuerst die Zeile anlegen, und zwar OHNE Verweise in den
 * Projektordner (scheitert spaeter etwas, zeigt die Vorlage nie auf Dateien,
 * die der Projekt-Editor loeschen darf; die Storage-Policy verlangt ohnehin
 * die Zeile, bevor in `vorlagen/<id>/` geschrieben wird). Dann jede Datei
 * kopieren (kopiereMedium); scheitert eine, faellt ihr Verweis heraus und ein
 * Klartext-Hinweis kommt dazu — abgebrochen wird nicht. Zum Schluss die
 * umgeschriebenen Pfade per updateVorlage eintragen (Fassung 2: jeder
 * Inhalts-Schreibvorgang zaehlt, sonst griffe die Sperre nicht).
 *
 * Verweise unter `vorlagen/` (die Kunden-Unterweisung ist selbst eine Kopie)
 * bleiben stehen: sie gehoeren einer freigegebenen Vorlage, deren Dateien
 * nie geloescht werden. Die Kundenfassung bleibt unveraendert.
 */
export const alsVorlageSpeichern = async (
  u: SifaUnterweisung,
): Promise<{ vorlage: SifaUnterweisungVorlage; hinweise: string[] }> => {
  if (!u || !(u.id || '').trim()) throw new Error('Keine Unterweisung gewählt.');
  const inhalt = u.inhalt || [];
  const zuKopieren = medienPfade(inhalt).filter((p) => !p.startsWith(`${VORLAGEN_ORDNER}/`));

  const vorlage = await createVorlage({
    titel: u.titel,
    beschreibung: u.beschreibung ?? null,
    kategorie: null,
    inhalt: pfadeUmschreiben(inhalt, new Map(zuKopieren.map((p) => [p, '']))),
    fragen: u.fragen || [],
    bestehenProzent: u.bestehenProzent,
    gueltigMonate: u.gueltigMonate,
    dauerMin: u.dauerMin ?? null,
    sprache: u.sprache,
  });

  const ordner = vorlagenOrdner(vorlage.id);
  const abbildung = new Map<string, string>();
  const hinweise: string[] = [];
  for (const alt of zuKopieren) {
    const istVideo = inhalt.some((a) => a?.videoPfad === alt);
    const neu = `${ordner}/${crypto.randomUUID()}.${endungFuer(alt, istVideo)}`;
    try {
      await kopiereMedium(alt, neu);
      abbildung.set(alt, neu);
    } catch (e) {
      abbildung.set(alt, '');
      hinweise.push(`${beschreibeDatei(inhalt, alt)} ließ sich nicht übernehmen (${describeError(e)}). In der Vorlage fehlt die Datei — bitte dort neu hochladen.`);
    }
  }

  // Nichts kopiert: der angelegte Stand ist schon der richtige.
  if (![...abbildung.values()].some(Boolean)) return { vorlage, hinweise };

  try {
    const fertig = await updateVorlage(vorlage.id, vorlage.fassung, { inhalt: pfadeUmschreiben(inhalt, abbildung) });
    return { vorlage: fertig, hinweise };
  } catch (e) {
    hinweise.push(`Die Dateien wurden kopiert, aber nicht in die Vorlage eingetragen (${describeError(e)}). Bitte die Vorlage prüfen.`);
    return { vorlage, hinweise };
  }
};
